import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaCreditCard, 
  FaRupeeSign, 
  FaCalendarAlt, 
  FaHandHoldingHeart, 
  FaQrcode,
  FaDownload,
  FaHistory,
  FaUserCircle,
  FaShieldAlt,
  FaLock,
  FaMobileAlt,
  FaUniversity,
  FaReceipt,
  FaCheckCircle
} from 'react-icons/fa';
import { GiPayMoney, GiReceiveMoney } from 'react-icons/gi';
import { useLanguage } from '../context/LanguageContext';

const DonationPage: React.FC = () => {
  const location = useLocation();
  const { t } = useLanguage();
  const [donationType, setDonationType] = useState<'one-time' | 'monthly'>('one-time');
  const [amount, setAmount] = useState<number>(500);
  const [selectedCampaign, setSelectedCampaign] = useState<string>('general');
  
  // Map service IDs to campaign IDs
  const serviceTocampaignMap: { [key: string]: string } = {
    'tree': 'tree',
    'cow': 'cow',
    'girls': 'education',
    'disaster': 'disaster',
    'oldage': 'elderly',
    'meditation': 'general',
    'heritage': 'general',
    'legal': 'general',
    'organic': 'general'
  };

  // Auto-select campaign if service was selected
  useEffect(() => {
    const state = location.state as { selectedService?: string } | null;
    if (state?.selectedService) {
      const campaignId = serviceTocampaignMap[state.selectedService] || 'general';
      setSelectedCampaign(campaignId);
    }
  }, [location]);
  
  const campaigns = [
    { id: 'general', nameKey: 'generalFund', descKey: 'generalFundDesc' },
    { id: 'tree', nameKey: 'treePlantationCampaign', descKey: 'treePlantationDesc' },
    { id: 'education', nameKey: 'girlsEducationCampaign', descKey: 'girlsEducationDesc' },
    { id: 'cow', nameKey: 'cowWelfareCampaign', descKey: 'cowWelfareDesc' },
    { id: 'disaster', nameKey: 'disasterReliefCampaign', descKey: 'disasterReliefDesc' },
    { id: 'elderly', nameKey: 'elderlyCareCampaign', descKey: 'elderlyCareDesc' }
  ];

  const presetAmounts = [100, 500, 1000, 2000, 5000, 10000];
  const paymentMethods = [
    { id: 'razorpay', name: 'Razorpay', icon: <FaCreditCard />, description: 'Secure online payment gateway' },
    { id: 'upi', name: 'UPI Payment', icon: <FaMobileAlt />, description: 'Instant UPI payments' },
    { id: 'card', name: 'Credit/Debit Card', icon: <FaCreditCard />, description: 'Visa, Mastercard, RuPay' },
    { id: 'netbanking', name: 'Net Banking', icon: <FaUniversity />, description: 'All major Indian banks' },
    { id: 'qr', name: 'QR Code', icon: <FaQrcode />, description: 'Scan and pay with any UPI app' }
  ];

  const handleDonation = () => {
    // In a real application, this would integrate with Razorpay or other payment gateway
    const campaign = campaigns.find(c => c.id === selectedCampaign);
    const campaignName = campaign ? t(campaign.nameKey) : '';
    alert(`Thank you for your donation of ₹${amount} to ${campaignName}. You will be redirected to secure payment.`);
  };

  return (
    <div className="donation-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-green to-primary-blue py-12">
        <div className="container-lg px-4 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('makeADifferenceToday')}</h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              {t('donationHeroSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* QR Code Donation - Moved to Top */}
      <section className="py-6 md:py-10 bg-gray-50">
        <div className="container-lg px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6">{t('scanAndDonate')}</h2>
              <p className="text-gray-700 mb-6">
                {t('scanAndDonateDesc')}
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                  <span>{t('instantPaymentConfirmation')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                  <span>{t('noRegistrationRequired')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                  <span>{t('worksWithAllUPI')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                  <span>{t('autoGeneratedReceipt')}</span>
                </li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-col items-center"
            >
              <div className="bg-white p-8 rounded-2xl shadow-xl">
                {/* QR Code Image */}
                <div className="w-96 h-96 mb-6 flex items-center justify-center">
                  <img 
                    src="/qrscanner.png" 
                    alt="QR Code for Donation" 
                    className="w-full h-full object-contain rounded-lg"
                  />
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-800 mb-2">{t('scanToDonate')}</div>
                  <div className="font-semibold text-gray-700">{t('upiId')}: vishwabandhu@upi</div>
                  <div className="text-sm text-gray-600">{t('useAnyUPIApp')}</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="section-padding">
        <div className="container-lg px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Donation Options */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
                <h2 className="text-2xl font-bold mb-6">{t('makeYourDonation')}</h2>
                
                {/* Donation Type */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <span className="mr-2 text-primary-green"><FaCalendarAlt /></span> {t('donationType')}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setDonationType('one-time')}
                      className={`p-4 rounded-xl border-2 transition-all ${donationType === 'one-time' ? 'border-primary-green bg-green-50' : 'border-gray-200 hover:border-primary-green'}`}
                    >
                      <div className="text-center">
                        <span className={`text-3xl mx-auto mb-2 flex justify-center ${donationType === 'one-time' ? 'text-primary-green' : 'text-gray-400'}`}><GiPayMoney /></span>
                        <div className={`font-semibold ${donationType === 'one-time' ? 'text-primary-green' : 'text-gray-700'}`}>
                          {t('oneTimeDonation')}
                        </div>
                        <div className="text-sm text-gray-600">{t('singleContribution')}</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => setDonationType('monthly')}
                      className={`p-4 rounded-xl border-2 transition-all ${donationType === 'monthly' ? 'border-primary-green bg-green-50' : 'border-gray-200 hover:border-primary-green'}`}
                    >
                      <div className="text-center">
                        <span className={`text-3xl mx-auto mb-2 flex justify-center ${donationType === 'monthly' ? 'text-primary-green' : 'text-gray-400'}`}><GiReceiveMoney /></span>
                        <div className={`font-semibold ${donationType === 'monthly' ? 'text-primary-green' : 'text-gray-700'}`}>
                          {t('monthlyDonation')}
                        </div>
                        <div className="text-sm text-gray-600">{t('recurringContribution')}</div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Campaign Selection */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <span className="mr-2 text-primary-green"><FaHandHoldingHeart /></span> {t('selectCampaign')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {campaigns.map((campaign) => (
                      <button
                        key={campaign.id}
                        onClick={() => setSelectedCampaign(campaign.id)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${selectedCampaign === campaign.id ? 'border-primary-green bg-green-50' : 'border-gray-200 hover:border-primary-green'}`}
                      >
                        <div className="font-semibold text-gray-800 mb-1">{t(campaign.nameKey)}</div>
                        <div className="text-sm text-gray-600">{t(campaign.descKey)}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Security Note */}
                <div className="mt-6 p-4 bg-green-50 rounded-lg flex items-start">
                  <span className="text-primary-green text-xl mr-3 mt-1"><FaShieldAlt /></span>
                  <div className="text-sm text-gray-700">
                    <span className="font-semibold">{t('secureEncrypted')}</span> {t('secureEncryptedDesc')}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Donation Summary & Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Tax Benefits */}
              <div className="bg-gradient-to-br from-primary-green/10 to-primary-blue/10 rounded-2xl p-6">
                <h3 className="text-xl font-bold mb-4">{t('taxBenefits')}</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                    <span className="text-gray-700">{t('taxExemption50')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                    <span className="text-gray-700">{t('instantDonationReceipts')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                    <span className="text-gray-700">{t('annualTaxCertificates')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-green mt-1 mr-2 flex-shrink-0"><FaCheckCircle /></span>
                    <span className="text-gray-700">{t('transparentFundUtilization')}</span>
                  </li>
                </ul>
              </div>

              {/* Donor Dashboard Preview */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <span className="mr-2 text-primary-green"><FaUserCircle /></span> {t('donorDashboard')}
                </h3>
                <p className="text-gray-600 mb-4">
                  {t('donorDashboardDesc')}
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <span className="text-gray-400 mr-3"><FaHistory /></span>
                    <span>{t('donationHistory')}</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-gray-400 mr-3"><FaDownload /></span>
                    <span>{t('downloadReceipts')}</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-gray-400 mr-3"><FaReceipt /></span>
                    <span>{t('taxExemptionCertificates')}</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-gray-400 mr-3"><FaUserCircle /></span>
                    <span>{t('profileManagement')}</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default DonationPage;